import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const MainLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { currentUser, userRole, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error("Erreur lors de la déconnexion:", error);
        }
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Sidebar mobile */}
            <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? 'visible' : 'invisible'}`} role="dialog" aria-modal="true">
                <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ${sidebarOpen ? 'opacity-100 ease-out duration-300' : 'opacity-0 ease-in duration-200'}`} aria-hidden="true"></div>

                <div className={`relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-indigo-700 transition transform ${sidebarOpen ? 'translate-x-0 ease-out duration-300' : '-translate-x-full ease-in duration-200'}`}>
                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                        <button
                            type="button"
                            className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <span className="sr-only">Fermer le menu</span>
                            <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex-shrink-0 flex items-center px-4">
                        <span className="text-white text-2xl font-bold">RécensementEglise</span>
                    </div>
                    <div className="mt-5 flex-1 h-0 overflow-y-auto">
                        <nav className="px-2 space-y-1">
                            <Link
                                to="/dashboard"
                                className={`${isActive('/dashboard') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                            >
                                <svg className="mr-4 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                Tableau de bord
                            </Link>

                            {(userRole?.role === 'admin' || userRole?.role === 'church_admin') && (
                                <Link
                                    to="/members"
                                    className={`${isActive('/members') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                                >
                                    <svg className="mr-4 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                    Membres
                                </Link>
                            )}

                            {(userRole?.role === 'admin' || userRole?.role === 'church_admin') && (
                                <Link
                                    to="/messages"
                                    className={`${isActive('/messages') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                                >
                                    <svg className="mr-4 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                    Messages
                                </Link>
                            )}

                            {userRole?.role === 'admin' && (
                                <Link
                                    to="/churches"
                                    className={`${isActive('/churches') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                                >
                                    <svg className="mr-4 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    Églises
                                </Link>
                            )}

                            {userRole?.role === 'admin' && (
                                <Link
                                    to="/admin/users"
                                    className={`${isActive('/admin/users') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                                >
                                    <svg className="mr-4 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                    Gestion des utilisateurs
                                </Link>
                            )}

                            <Link
                                to="/profile"
                                className={`${isActive('/profile') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                            >
                                <svg className="mr-4 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                Mon profil
                            </Link>

                            <button
                                onClick={handleLogout}
                                className="text-indigo-100 hover:bg-indigo-600 group flex items-center px-2 py-2 text-base font-medium rounded-md w-full text-left"
                            >
                                <svg className="mr-4 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                Déconnexion
                            </button>
                        </nav>
                    </div>
                </div>

                <div className="flex-shrink-0 w-14" aria-hidden="true">
                    {/* Force sidebar to shrink to fit close icon */}
                </div>
            </div>

            {/* Sidebar desktop */}
            <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
                <div className="flex-1 flex flex-col min-h-0 bg-indigo-700">
                    <div className="flex items-center h-16 flex-shrink-0 px-4 bg-indigo-800">
                        <span className="text-white text-xl font-bold">RécensementEglise</span>
                    </div>
                    <div className="flex-1 flex flex-col overflow-y-auto">
                        <nav className="flex-1 px-2 py-4 space-y-1">
                            <Link
                                to="/dashboard"
                                className={`${isActive('/dashboard') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                            >
                                <svg className="mr-3 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                Tableau de bord
                            </Link>

                            {(userRole?.role === 'admin' || userRole?.role === 'church_admin') && (
                                <Link
                                    to="/members"
                                    className={`${isActive('/members') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                                >
                                    <svg className="mr-3 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                    Membres
                                </Link>
                            )}

                            {(userRole?.role === 'admin' || userRole?.role === 'church_admin') && (
                                <Link
                                    to="/messages"
                                    className={`${isActive('/messages') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                                >
                                    <svg className="mr-3 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                    Messages
                                </Link>
                            )}

                            {userRole?.role === 'admin' && (
                                <Link
                                    to="/churches"
                                    className={`${isActive('/churches') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                                >
                                    <svg className="mr-3 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    Églises
                                </Link>
                            )}

                            {userRole?.role === 'admin' && (
                                <Link
                                    to="/admin/users"
                                    className={`${isActive('/admin/users') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                                >
                                    <svg className="mr-3 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                    Gestion des utilisateurs
                                </Link>
                            )}

                            <Link
                                to="/profile"
                                className={`${isActive('/profile') ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                            >
                                <svg className="mr-3 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                Mon profil
                            </Link>

                            <button
                                onClick={handleLogout}
                                className="text-indigo-100 hover:bg-indigo-600 group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left"
                            >
                                <svg className="mr-3 h-6 w-6 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                Déconnexion
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="flex flex-col">
                <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
                    <button
                        type="button"
                        className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <span className="sr-only">Ouvrir le menu</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                    <div className="flex-1 px-4 flex justify-between">
                        <div className="flex-1 flex"></div>
                        <div className="ml-4 flex items-center md:ml-6">
                            <div className="flex items-center">
                                <span className="text-gray-700 mr-4 font-medium">{currentUser?.email}</span>
                                <Link to="/profile" className="bg-indigo-100 text-indigo-800 p-1 rounded-full">
                                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <main className="flex-1">
                    <div className="py-6">
                        <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
                            <Outlet />
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MainLayout;
